import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Sparkles,
  Info,
  RefreshCcw,
  ChevronRight,
  Presentation,
  Coins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import KPICards from "@/components/charts/KPICards";
import TaxBreakdownDonut from "@/components/charts/TaxBreakdownDonut";
import TaxCalendar from "@/components/charts/TaxCalendar";
import BudgetTreemap from "@/components/charts/BudgetTreemap";
import SectorRadar from "@/components/charts/SectorRadar";
import ProfileCompareBar from "@/components/charts/ProfileCompareBar";
import DebtEvolutionLine from "@/components/charts/DebtEvolutionLine";
import ProfileSwitcher from "@/components/charts/ProfileSwitcher";
import PresentationMode from "@/components/presentation/PresentationMode";
import { getProject } from "@/lib/profiles";
import { getLatestAnalysis } from "@/lib/analyses";
import {
  fmtMoney,
  fmtDays,
  fmtYears,
} from "@/lib/chart-utils";

export default function WizardResults() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [presenting, setPresenting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [proj, an] = await Promise.all([
        getProject(projectId),
        getLatestAnalysis(projectId),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setAnalysis(an);
      if (an?.results?.profiles?.length) {
        setActiveProfileId(an.results.profiles[0].profile_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger l'analyse.");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const results = analysis?.results || null;
  const profiles = results?.profiles || [];
  const activeProfile = useMemo(
    () => profiles.find((p) => p.profile_id === activeProfileId) || profiles[0] || null,
    [profiles, activeProfileId]
  );
  const ind = activeProfile?.individual;
  const currency = results?.summary?.currency || "UM";
  const salaireJournalierMoyen =
    results?.national?.salaire_journalier_moyen ||
    ind?.salaire_journalier ||
    null;

  if (loading) {
    return (
      <div
        className="flex min-h-[50vh] items-center justify-center"
        data-testid="wizard-results-loading"
      >
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="space-y-6" data-testid="wizard-results-empty">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Nouveau projet · Étape 4/7
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
            Dashboard des résultats
          </h1>
        </div>
        <WizardStepper current={4} />
        <Card data-testid="no-analysis-card" className="border-dashed">
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <Info className="h-10 w-10 text-muted-foreground" />
            <h3 className="text-lg font-semibold">Aucune analyse disponible</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Lancez d'abord une analyse vitaliste à l'étape 3 pour voir le
              tableau de bord.
            </p>
            <Button
              onClick={() =>
                navigate(`/app/projects/${projectId}/wizard/confirm`)
              }
              data-testid="go-back-confirm-btn"
            >
              Aller à l'étape 3
            </Button>
            <div className="mt-2 border-t border-border pt-4 text-xs text-muted-foreground">
              <p>Le module DEBT-VITA™ est aussi accessible directement, indépendamment de l'analyse vitaliste principale :</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/app/projects/${projectId}/debt`)}
                data-testid="open-debt-vita-btn"
                className="mt-2"
              >
                <Coins className="mr-2 h-4 w-4" />
                Dette & Vies
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wizard-results-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Nouveau projet · Étape 4/7
          </div>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
            Dashboard des résultats vitalistes
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            {project?.name}
            {project?.fiscal_year && ` · Exercice ${project.fiscal_year}`}
            {" "}— {profiles.length} profil{profiles.length > 1 ? "s" : ""} analysé
            {profiles.length > 1 ? "s" : ""}
            {currency && ` · devise ${currency}`}.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setPresenting(true)}
          data-testid="open-presentation-btn"
          className="whitespace-nowrap"
        >
          <Presentation className="mr-2 h-4 w-4" />
          Mode présentation
        </Button>
        <Button
          onClick={() => navigate(`/app/projects/${projectId}/debt`)}
          data-testid="open-debt-vita-btn"
          className="whitespace-nowrap bg-red-700 text-white hover:bg-red-800"
        >
          <Coins className="mr-2 h-4 w-4" />
          Dette & Vies
        </Button>
      </div>

      <WizardStepper current={4} />

      <TutorialDeepLink
        chapter={5}
        section="section-5-1"
        id="wizard-step-4-results"
        label="Décrypter les six graphiques du dashboard vitaliste"
        duration={8}
      />

      {/* Profile switcher */}
      {profiles.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ProfileSwitcher
            profiles={profiles}
            activeId={activeProfileId}
            onChange={setActiveProfileId}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            data-testid="refresh-analysis-btn"
          >
            <RefreshCcw className="mr-1 h-3.5 w-3.5" />
            Rafraîchir
          </Button>
        </div>
      )}

      {/* KPIs */}
      <KPICards ind={ind} debt={results.debt} />

      {/* Macro summary strip */}
      <Card data-testid="macro-summary" className="border-brand-gold/20">
        <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Total prélèvements / an
            </div>
            <div
              className="mt-1 font-mono text-lg font-bold"
              data-testid="macro-total-prelevements"
            >
              {fmtMoney(ind?.total_prelevements, currency)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Budget national converti
            </div>
            <div
              className="mt-1 font-mono text-lg font-bold"
              data-testid="macro-national-years"
            >
              {results.national?.available
                ? fmtYears(results.national.annees_vies_budget)
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Salaire journalier (profil courant)
            </div>
            <div
              className="mt-1 font-mono text-lg font-bold"
              data-testid="macro-daily-wage"
            >
              {fmtMoney(ind?.salaire_journalier, currency)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grid of charts */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <TaxBreakdownDonut ind={ind} currency={currency} />
        <TaxCalendar ind={ind} />
      </div>

      <BudgetTreemap
        aggregatedBudgetLines={results.aggregated_budget_lines || []}
        salaireJournalierMoyen={salaireJournalierMoyen}
        currency={currency}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <SectorRadar
          sectorsResult={results.sectors}
          aggregatedBudgetLines={results.aggregated_budget_lines || []}
          salaireJournalierMoyen={salaireJournalierMoyen}
        />
        <ProfileCompareBar profiles={profiles} activeId={activeProfileId} />
      </div>

      <DebtEvolutionLine
        multiYearPoints={results.multi_year_points || []}
        populationActive={analysis.inputs?.population_active}
        salaireJournalierMoyen={salaireJournalierMoyen}
        currency={currency}
      />

      {/* Insight callouts */}
      {results.summary?.most_taxed_profile_name && profiles.length > 1 && (
        <Card
          className="border-destructive/30 bg-destructive/5"
          data-testid="insight-callout"
        >
          <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-destructive" />
              <div>
                <div className="text-xs uppercase tracking-widest text-destructive/80">
                  Constat vitaliste
                </div>
                <div className="mt-1 text-sm">
                  Parmi les profils comparés,{" "}
                  <strong>{results.summary.most_taxed_profile_name}</strong>{" "}
                  subit la plus lourde amputation vitaliste&nbsp;:{" "}
                  <span className="font-mono font-bold">
                    {fmtDays(results.summary.most_taxed_days_per_year)}
                  </span>{" "}
                  de vie consacrées chaque année à l'État.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/confirm`)
          }
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(`/app/projects/${projectId}/simulator`)
            }
            data-testid="wizard-goto-simulator-btn"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Simulateur & analyses avancées
          </Button>
          <Button
            variant="default"
            onClick={() =>
              navigate(`/app/projects/${projectId}/wizard/legal`)
            }
            data-testid="wizard-next-legal-btn"
          >
            Analyse juridique
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Presentation mode overlay */}
      {presenting && (
        <PresentationMode
          project={project}
          analysis={analysis}
          onClose={() => setPresenting(false)}
        />
      )}
    </div>
  );
}
